import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import DataTable from './DataTable';
import FormModal from './FormModal';
import { getTransportOptions, createTransportOption, updateTransportOption, deleteTransportOption, getDestinations } from '../../../services/travelInventoryService';

const TransportManager = ({ searchQuery }) => {
  const [transport, setTransport] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [transportData, destinationsData] = await Promise.all([
        getTransportOptions(),
        getDestinations(),
      ]);
      setTransport(transportData || []);
      setDestinations(destinationsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this transport option?')) return;
    
    try {
      await deleteTransportOption(id);
      await loadData();
    } catch (error) {
      console.error('Error deleting transport:', error);
      alert('Failed to delete transport option');
    }
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingItem) {
        await updateTransportOption(editingItem?.id, formData);
      } else {
        await createTransportOption(formData);
      }
      setIsModalOpen(false);
      await loadData();
    } catch (error) {
      console.error('Error saving transport:', error);
      throw error;
    }
  };

  const columns = [
    { key: 'provider_name', label: 'Provider', sortable: true },
    { key: 'transport_type', label: 'Type', sortable: true },
    { key: 'departure_location', label: 'From', sortable: true },
    { key: 'arrival_location', label: 'To', sortable: true },
    { key: 'price', label: 'Price', type: 'currency' },
  ];

  const formFields = [
    {
      name: 'origin_destination_id',
      label: 'Origin Destination',
      type: 'select',
      options: destinations?.map((d) => ({ value: d?.id, label: `${d?.name}, ${d?.country}` })),
    },
    {
      name: 'destination_destination_id',
      label: 'Destination',
      type: 'select',
      options: destinations?.map((d) => ({ value: d?.id, label: `${d?.name}, ${d?.country}` })),
    },
    {
      name: 'transport_type',
      label: 'Type',
      type: 'select',
      required: true,
      options: [
        { value: 'flight', label: 'Flight' },
        { value: 'train', label: 'Train' },
        { value: 'bus', label: 'Bus' },
        { value: 'car_rental', label: 'Car Rental' },
        { value: 'taxi', label: 'Taxi' },
        { value: 'ferry', label: 'Ferry' },
        { value: 'cruise', label: 'Cruise' },
      ],
    },
    { name: 'provider_name', label: 'Provider Name', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'textarea' },
    { name: 'departure_location', label: 'Departure Location', type: 'text' },
    { name: 'arrival_location', label: 'Arrival Location', type: 'text' },
    { name: 'duration_minutes', label: 'Duration (minutes)', type: 'number' },
    { name: 'price', label: 'Price', type: 'number', step: '0.01', required: true },
    { name: 'currency', label: 'Currency', type: 'text', defaultValue: 'PHP' },
    {
      name: 'price_range',
      label: 'Price Range',
      type: 'select',
      options: [
        { value: 'budget', label: 'Budget' },
        { value: 'moderate', label: 'Moderate' },
        { value: 'expensive', label: 'Expensive' },
        { value: 'luxury', label: 'Luxury' },
      ],
    },
    { name: 'image_url', label: 'Transport Image', type: 'image-upload' },
    { name: 'image_alt', label: 'Image Alt Text', type: 'text' },
    { name: 'website', label: 'Website', type: 'text' },
    { name: 'phone', label: 'Phone', type: 'text' },
    { name: 'email', label: 'Email', type: 'email' },
    { name: 'booking_url', label: 'Booking URL', type: 'text' },
    {
      name: 'features',
      label: 'Features',
      type: 'checkbox-group',
      options: [
        { value: 'wifi', label: 'WiFi Available' },
        { value: 'air_conditioning', label: 'Air Conditioning' },
        { value: 'luggage_storage', label: 'Luggage Storage' },
        { value: 'meals_included', label: 'Meals Included' },
        { value: 'entertainment', label: 'Entertainment System' },
        { value: 'wheelchair_accessible', label: 'Wheelchair Accessible' },
        { value: 'pet_friendly', label: 'Pet Friendly' },
        { value: 'power_outlets', label: 'Power Outlets' },
      ],
    },
    { name: 'is_featured', label: 'Featured', type: 'checkbox' },
  ];

  const filteredData = transport?.filter((item) => {
    const matchesSearch = searchQuery
      ? item?.provider_name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      : true;
    const matchesFilter = filterType === 'all' || item?.transport_type === filterType;
    return matchesSearch && matchesFilter;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Transportation</h2>
          <p className="text-gray-600 mt-1">Manage transport options and providers</p>
        </div>
        <Button iconName="Plus" onClick={handleCreate}>
          Add Transport
        </Button>
      </div>

      <div className="mb-4">
        <Select
          value={filterType}
          onChange={setFilterType}
          options={[
            { value: 'all', label: 'All Types' },
            { value: 'flight', label: 'Flight' },
            { value: 'train', label: 'Train' },
            { value: 'bus', label: 'Bus' },
            { value: 'ferry', label: 'Ferry' },
          ]}
          className="max-w-xs"
        />
      </div>

      <DataTable
        data={filteredData}
        columns={columns}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <FormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        title={editingItem ? 'Edit Transport' : 'Add Transport'}
        fields={formFields}
        initialData={editingItem}
      />
    </div>
  );
};

export default TransportManager;